import React from "react";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import {ch} from "@renta-apps/athenaeum-react-common";
import {Button, ButtonType, DateInput, Dropdown, DropdownOrderBy, TextAreaInput, TwoColumns} from "@renta-apps/athenaeum-react-components";
import CatalogItem from "../CatalogItem/CatalogItem";
import EnumProvider from "@/providers/EnumProvider";
import FeatureFlagsAwareEnumProvider from "@/providers/FeatureFlagsAwareEnumProvider";
import UnleashHelper from "@/helpers/UnleashHelper";
import FeatureFlags from "@/helpers/FeatureFlags";
import WorkOrderRentalItemModel from "@/models/server/WorkOrderRentalItemModel";
import Localizer from "@/localization/Localizer";
import {RentalItemActionType, WorkOrderContractType} from "@/models/Enums";

import styles from "./RentalEquipmentItem.module.scss";

export interface IRentalEquipmentItemProps {
    rentalEquipment: WorkOrderRentalItemModel;
    className?: string;
    editing?: boolean;
    expanded?: boolean;
    shoppingCart?: boolean;
    contractType?: WorkOrderContractType;
    onChange(sender: RentalEquipmentItem, rentalEquipment: WorkOrderRentalItemModel): Promise<void>;
    onRemove(sender: RentalEquipmentItem, rentalEquipment: WorkOrderRentalItemModel): Promise<void>;
    onDescriptionToggle?(sender: RentalEquipmentItem, rentalEquipment: WorkOrderRentalItemModel, expanded: boolean): Promise<void>;
}

interface IRentalEquipmentItemState {
    expanded: boolean;
}

export default class RentalEquipmentItem extends React.Component<IRentalEquipmentItemProps, IRentalEquipmentItemState> {

    // Inherited / implemented

    public state: IRentalEquipmentItemState = {
        expanded: (this.props.expanded === true),
    }

    // Getters

    private get rentalEquipment(): WorkOrderRentalItemModel {
        return this.props.rentalEquipment;
    }

    private get shoppingCart(): boolean {
        return (this.props.shoppingCart === true);
    }

    /**
     * Action types which permanently finalize a field service rental item.
     * Keep in sync with WorkOrderService.IsFieldServiceFinalActionType.
     */
    private static isFinalActionType(actionType: RentalItemActionType | undefined): boolean {
        return (actionType === RentalItemActionType.ServiceDone)
            || (actionType === RentalItemActionType.RequiresReplacement);
    }

    private get isFieldServiceWorkOrder(): boolean {
        return UnleashHelper.isEnabled(FeatureFlags.SendFieldServiceWorkOrders)
            && (this.props.contractType === WorkOrderContractType.FieldService);
    }

    private get isFinalized(): boolean {
        // The catalog keeps rental items in memory until the whole work order is saved, so the
        // action type itself has to block further changes - isFieldServiceFinalized only arrives
        // from the backend after a save.
        return (this.rentalEquipment?.isFieldServiceFinalized === true)
            || ((this.isFieldServiceWorkOrder) && (RentalEquipmentItem.isFinalActionType(this.rentalEquipment?.actionType)));
    }

    // Async-methods

    private async onChangeDescriptionAsync(comment: string): Promise<void> {
        if (this.rentalEquipment.comment !== comment) {
            this.rentalEquipment.comment = comment;
        }

        await this.props.onChange(this, this.rentalEquipment);
    }

    private async onChangeRentDateAsync(rentDate: Date): Promise<void> {
        if (this.rentalEquipment.rentDate !== rentDate) {
            this.rentalEquipment.rentDate = rentDate;
        }

        await this.props.onChange(this, this.rentalEquipment);
    }

    private async onChangeActionTypeAsync(actionType: RentalItemActionType): Promise<void> {
        if (this.rentalEquipment.actionType !== actionType) {
            const isFinalTransition: boolean = (this.isFieldServiceWorkOrder)
                && (RentalEquipmentItem.isFinalActionType(actionType));

            if (isFinalTransition) {
                const statusName: string = EnumProvider.getRentalItemActionTypeText(actionType);
                const message: string = Localizer.fieldServiceFinalStatusConfirmationTitle + "\n\n" +
                    Localizer.get(Localizer.fieldServiceFinalStatusConfirmationMessageLanguageItemName, statusName);
                const confirmed: boolean = await ch.confirmAsync(message);
                if (!confirmed) {
                    return;
                }
            }

            this.rentalEquipment.actionType = actionType;
        }

        await this.props.onChange(this, this.rentalEquipment);
    }

    private async toggleAsync(): Promise<void> {
        const expanded: boolean = (!this.state.expanded);

        await this.setState({expanded});

        if (this.props.onDescriptionToggle) {
            await this.props.onDescriptionToggle(this, this.rentalEquipment, expanded);
        }
    }

    private async removeAsync(): Promise<void> {
        await this.props.onRemove(this, this.rentalEquipment);
    }
    
    // Renders

    private get renderNormal(): JSX.Element {
        const rentalItemName: string = this.rentalEquipment.rentalItemName!;
        const rentalItemExternalId: string = this.rentalEquipment.rentalItemExternalId;
        const actionTypeName: string = EnumProvider.getRentalItemActionTypeText(this.rentalEquipment.actionType);

        const descriptionButtonIconName: string = (this.rentalEquipment.comment)
            ? "far comment-alt-dots"
            : "far comment-alt"

        return (
            <React.Fragment>

                <div className={styles.title}>

                    <div className={styles.name}>
                        <span>
                             {rentalItemName}{rentalItemExternalId ? ` – ${rentalItemExternalId}` : ""} ({actionTypeName})
                        </span>
                    </div>

                </div>

                <div className={styles.content}>

                    <div className={styles.actions}>
                        <div>
                            {
                                (!this.isFinalized) &&
                                (
                                    <Button block
                                            type={ButtonType.Orange}
                                            icon={{name: "fas minus"}}
                                            onClick={() => this.removeAsync()}
                                    />
                                )
                            }
                        </div>
                    </div>

                    <div className={styles.data}>

                        <div className={styles.description}>
                            <span>
                                {this.rentalEquipment.comment}
                            </span>
                        </div>

                    </div>

                    <div className={styles.actions}>

                        <div>
                            <Button block
                                    type={ButtonType.Orange}
                                    icon={{name: descriptionButtonIconName}}
                                    onClick={() => this.toggleAsync()}
                            />
                        </div>

                    </div>

                </div>

                <div className={styles.descriptionInput}>
                    {
                        (this.state.expanded) &&
                        (
                            <TextAreaInput autoFocus
                                           readonly={this.isFinalized}
                                           value={this.rentalEquipment.comment || ""}
                                           onChange={(_, value) => this.onChangeDescriptionAsync(value)}
                            />
                        )
                    }
                </div>

                <TwoColumns>
                    <DateInput required
                               readonly={this.isFinalized}
                               label={Localizer.genericDate}
                               value={this.rentalEquipment.rentDate}
                               onChange={async (value) => await this.onChangeRentDateAsync(value)}
                    />

                    <Dropdown required
                              disabled={this.isFinalized}
                              label={Localizer.addExtraChargeModalExtraChargeTypeDropdownLabel}
                              minWidth="105px"
                              orderBy={DropdownOrderBy.None}
                              items={FeatureFlagsAwareEnumProvider.getIndividualRentalItemActionTypeItems(this.props.contractType ?? WorkOrderContractType.Normal, false, this.rentalEquipment.actionType)}
                              selectedItem={this.rentalEquipment.actionType}
                              onChange={async (_, item) => await this.onChangeActionTypeAsync(parseInt(item!.value))}
                    />
                </TwoColumns>
                
            </React.Fragment>
        );
    }

    public render(): React.ReactNode {
        const className: string = Utility.css(
            styles.rentalEquipment,
            this.props.className,
            (this.shoppingCart) && (styles.active));

        return (
            <CatalogItem className={className}>
                {
                    this.renderNormal
                }
            </CatalogItem>
        );
    }
};